import React, { useState } from 'react';
import { signInWithEmailAndPassword } from 'firebase/auth';
import { auth, setAdminSession } from '../firebase/firebaseConfig.jsx';
import { Alert, Input, Button } from 'antd';
import { motion } from 'framer-motion';
import {
  LockClosedIcon,
  EnvelopeIcon,
  ArrowRightIcon,
  ShieldCheckIcon,
} from '@heroicons/react/24/outline';
import './AdminLoginPage.css';

const ADMIN_EMAIL_ALLOWLIST = new Set(['kairos@kairos.com', 'admin@kairos.com']);

const AdminLoginPage = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleLogin = async (e) => {
    e.preventDefault();

    const trimmedEmail = email.trim();
    const trimmedPassword = password.trim();

    console.log('🔍 로그인 시작...', {
      email: trimmedEmail,
      hasPassword: !!trimmedPassword,
      passwordLength: trimmedPassword.length,
      authAppName: auth.app.name,
      authConfig: auth.app.options
    });

    if (!trimmedEmail || !trimmedPassword) {
      setError('이메일과 비밀번호를 모두 입력해주세요.');
      return;
    }

    setError('');
    setLoading(true);

    try {
      console.log('📝 Admin 세션 마커 설정...');
      setAdminSession();
      console.log('✅ Admin 세션 마커 설정 완료');

      console.log('🔥 Firebase Auth 시도...', {
        email: trimmedEmail,
        authDomain: auth.app.options.authDomain,
        projectId: auth.app.options.projectId
      });

      const userCredential = await signInWithEmailAndPassword(auth, trimmedEmail, trimmedPassword);
      const user = userCredential.user;
      console.log('✅ Firebase 로그인 성공:', user.email);

      const token = await user.getIdTokenResult();
      console.log('🔍 Custom claims:', token.claims);

      const isAllowlistedEmail = ADMIN_EMAIL_ALLOWLIST.has(trimmedEmail.toLowerCase());
      if (token.claims.role === 'admin' || token.claims.admin === true || isAllowlistedEmail) {
        console.log('✅ Admin 로그인 성공');
        const next = new URLSearchParams(window.location.search).get('next') || '/';
        window.location.href = next.startsWith('/') && !next.startsWith('//') ? next : '/';
      } else {
        console.log('❌ Admin 권한 없음, 로그아웃 중...');
        await auth.signOut();
        setError('관리자 권한이 없습니다.');
      }
    } catch (error) {
      console.error('❌ 로그인 실패:', error);
      console.error('❌ Error code:', error.code);
      console.error('❌ Error message:', error.message);

      switch (error.code) {
        case 'auth/invalid-email':
          setError('유효하지 않은 이메일입니다.');
          break;
        case 'auth/user-disabled':
          setError('비활성화된 사용자입니다.');
          break;
        case 'auth/user-not-found':
          setError('사용자를 찾을 수 없습니다. 이메일을 확인해주세요.');
          break;
        case 'auth/wrong-password':
          setError('비밀번호가 올바르지 않습니다.');
          break;
        case 'auth/invalid-credential':
          setError('이메일 또는 비밀번호가 올바르지 않습니다. 다시 확인해주세요.');
          break;
        case 'auth/too-many-requests':
          setError('너무 많은 로그인 시도가 있었습니다. 잠시 후 다시 시도해주세요.');
          break;
        case 'auth/network-request-failed':
          setError('네트워크 연결을 확인해주세요.');
          break;
        default:
          setError(`로그인 실패: ${error.message}`);
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="admin-login-page">
      <div className="admin-login-page__mesh" aria-hidden />
      <div className="admin-login-page__orb admin-login-page__orb--1" aria-hidden />
      <div className="admin-login-page__orb admin-login-page__orb--2" aria-hidden />
      <div className="admin-login-page__orb admin-login-page__orb--3" aria-hidden />
      <div className="admin-login-page__grid" aria-hidden />

      <motion.div
        className="admin-login-page__shell"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
      >
        <header className="admin-login-page__header">
          <div className="admin-login-page__brand">
            <span className="admin-login-page__brand-mark">Kairos</span>
            <span className="admin-login-page__brand-badge">Admin</span>
          </div>
          <h1 className="admin-login-page__title">운영 콘솔</h1>
          <p className="admin-login-page__lead">
            승인된 관리자만 접근할 수 있습니다.
          </p>
          <div className="admin-login-page__trust">
            <ShieldCheckIcon className="admin-login-page__trust-icon" aria-hidden />
            <span>세션은 암호화되어 전송됩니다</span>
          </div>
        </header>

        <form onSubmit={handleLogin} className="admin-login-page__form">
          {error && (
            <Alert
              message={error}
              type="error"
              showIcon
              closable
              onClose={() => setError('')}
              className="admin-login-page__alert"
            />
          )}

          <label className="admin-login-page__label" htmlFor="admin-login-email">
            이메일
          </label>
          <Input
            id="admin-login-email"
            type="email"
            placeholder="name@organization.com"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            prefix={<EnvelopeIcon className="admin-login-page__input-icon" />}
            size="large"
            autoFocus
            required
            autoComplete="username"
            className="admin-login-page__input"
          />

          <label className="admin-login-page__label" htmlFor="admin-login-password">
            비밀번호
          </label>
          <Input.Password
            id="admin-login-password"
            placeholder="••••••••"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            prefix={<LockClosedIcon className="admin-login-page__input-icon" />}
            size="large"
            required
            autoComplete="current-password"
            className="admin-login-page__input"
          />

          <Button
            type="primary"
            htmlType="submit"
            size="large"
            block
            loading={loading}
            className="admin-login-page__submit"
          >
            <span className="admin-login-page__submit-inner">
              {loading ? '확인 중…' : '로그인'}
              {!loading && (
                <ArrowRightIcon className="admin-login-page__submit-icon" aria-hidden />
              )}
            </span>
          </Button>
        </form>

        <footer className="admin-login-page__footer">
          <p className="admin-login-page__footer-note">
            © {new Date().getFullYear()} Kairos Platform · 내부 운영 전용
          </p>
        </footer>
      </motion.div>
    </div>
  );
};

export default AdminLoginPage;
